import { useState } from "react";
import type { ChangeEvent } from "react";
import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EMPTY_VALUE } from "@/constants";
import { formatCurrency, orEmptyValue } from "@/lib/hcbb-cost-estimator-utils";
import type { HcbbCostSearch } from "@/lib/hcbb-cost-estimator-utils";
import { useHcbbFacilityProviders } from "@/hooks/use-hcbb-cost-estimator";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import { HcbbTablePagination } from "@/pages/components/hcbb-cost-estimator/hcbb-table-pagination";
import type {
  HcbbFacilityProvider,
  HcbbFacilityResult,
  HcbbPriceHighlight,
} from "@/model/hcbb-cost-estimator";

const PROVIDERS_HEADING_ID = "hcbb-facility-providers-heading";
const PROVIDERS_SEARCH_ID = "hcbb-facility-providers-search";

/** Rows per page — the table pages client-side, so this is the whole page size. */
const PROVIDERS_PAGE_SIZE = 10;

/** Below this many letters, a term is still being typed rather than searched. */
const MIN_SEARCH_LENGTH = 3;

/**
 * The doctors working at this facility, priced for the same search that
 * found it.
 *
 * Self-contained, like the results list's own hooks: it takes the committed
 * search, the clicked facility and that same search's own priced benchmark,
 * and fetches its own answer rather than asking the page to hold a query
 * result on its behalf.
 */
export const HcbbFacilityProviders = ({
  search,
  facility,
  highlight,
}: {
  search: HcbbCostSearch;
  facility: HcbbFacilityResult;
  highlight: HcbbPriceHighlight | null;
}) => {
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const { data, isPending, isError, refetch } = useHcbbFacilityProviders(
    search,
    facility,
    highlight,
  );
  const providers = data?.results ?? [];

  // A term shorter than the minimum is still being typed, so it matches
  // everything rather than nothing — the list would otherwise empty out on
  // the first keystroke or two of every search.
  const query = term.trim().toLowerCase();
  const filteredProviders =
    query.length < MIN_SEARCH_LENGTH
      ? providers
      : providers.filter(
          (provider) =>
            provider.displayName.toLowerCase().includes(query) ||
            provider.specialty.toLowerCase().includes(query),
        );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProviders.length / PROVIDERS_PAGE_SIZE),
  );
  // Clamped rather than reset: a page a shorter list no longer has still
  // shows its last page instead of silently jumping back to the first.
  const currentPage = Math.min(page, totalPages);
  const visibleProviders = filteredProviders.slice(
    (currentPage - 1) * PROVIDERS_PAGE_SIZE,
    currentPage * PROVIDERS_PAGE_SIZE,
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTerm(event.target.value);
    // A new term reshapes the list, so page 2 of the old one would either
    // land past the end of the new one or show the wrong rows outright.
    setPage(1);
  };

  return (
    <section aria-labelledby={PROVIDERS_HEADING_ID} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id={PROVIDERS_HEADING_ID}
          className="text-sm font-medium text-gray-800"
        >
          Providers who practices at this facility:
        </h2>

        {/* Nothing to search until there is at least one doctor to find. */}
        {providers.length > 0 && (
          <div className="relative w-full max-w-56">
            <label htmlFor={PROVIDERS_SEARCH_ID} className="sr-only">
              Search providers by name or specialty
            </label>
            <Input
              id={PROVIDERS_SEARCH_ID}
              type="text"
              value={term}
              onChange={handleSearchChange}
              placeholder="Search"
              className="pr-9 text-sm"
            />
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-3 my-auto size-4 text-muted-foreground"
            />
          </div>
        )}
      </div>

      {isPending && <HcbbFacilityProvidersSkeleton facility={facility} />}

      {isError && (
        <div role="alert">
          <SomethingWentWrong message="We could not load the doctors at this facility." />
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </div>
      )}

      {!isPending && !isError && data && filteredProviders.length === 0 && (
        <NoDataAvailable
          title={
            providers.length === 0
              ? "No providers on file"
              : "No providers match your search"
          }
          message={
            providers.length === 0
              ? "No providers on file for this facility."
              : `No providers match "${term.trim()}".`
          }
        />
      )}

      {!isPending && !isError && data && filteredProviders.length > 0 && (
        <>
          <HcbbFacilityProvidersTable providers={visibleProviders} />

          {totalPages > 1 && (
            <HcbbTablePagination
              label="Doctors pagination"
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </section>
  );
};

/**
 * A row per doctor the endpoint returned, in a `role="status"` shell so a
 * screen reader hears the wait — the skeleton bars themselves stay
 * `aria-hidden`, same as the results list's own loading rows.
 */
const HcbbFacilityProvidersSkeleton = ({
  facility,
}: {
  facility: HcbbFacilityResult;
}) => (
  <div role="status">
    <p className="sr-only">
      Loading doctors for {facility.displayName || "this facility"}.
    </p>
    <div aria-hidden="true" className="space-y-2">
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full bg-gray-200" />
      ))}
    </div>
  </div>
);

/**
 * `networkName` and `tier` are read together as one label, as the offices
 * table does for a provider's own practice locations — showing only one half
 * once both land would misdescribe the network the other half names.
 */
const toNetworkLabel = (provider: HcbbFacilityProvider): string => {
  const network = orEmptyValue(provider.networkName);
  const tier = orEmptyValue(provider.tier);

  if (network === EMPTY_VALUE) return tier;
  if (tier === EMPTY_VALUE) return network;

  return `${network} - ${tier}`;
};

/** Callers only render this once `providers` is known to be non-empty. */
const HcbbFacilityProvidersTable = ({
  providers,
}: {
  providers: HcbbFacilityProvider[];
}) => {
  return (
    <Table aria-labelledby={PROVIDERS_HEADING_ID}>
      <TableHeader className="uppercase">
        <TableRow className="bg-primary hover:bg-primary">
          <TableHead scope="col" className="text-primary-foreground">
            Doctor
          </TableHead>
          <TableHead scope="col" className="text-primary-foreground">
            Specialty
          </TableHead>
          <TableHead scope="col" className="text-primary-foreground">
            Network
          </TableHead>
          <TableHead scope="col" className="text-primary-foreground">
            Distance
          </TableHead>
          <TableHead scope="col" className="text-primary-foreground">
            Cost
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {providers.map((provider, index) => {
          const networkLabel = toNetworkLabel(provider);

          return (
            // The endpoint answers with no id of its own, and a name can
            // repeat across two doctors — position is the only thing that
            // is guaranteed unique per row.
            <TableRow key={index}>
              <TableCell className="font-medium text-gray-800">
                {orEmptyValue(provider.displayName)}
              </TableCell>
              <TableCell>{orEmptyValue(provider.specialty)}</TableCell>
              <TableCell>
                {networkLabel === EMPTY_VALUE ? (
                  networkLabel
                ) : (
                  <Badge className="rounded-full bg-green-600 font-light text-white">
                    {networkLabel}
                  </Badge>
                )}
              </TableCell>
              <TableCell>{orEmptyValue(provider.distance)}</TableCell>
              <TableCell>{formatCurrency(provider.estimatedCost)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
